import { supabase } from "@/integrations/supabase/client";

/**
 * Pulls the real error text out of a supabase-js FunctionsHttpError. The
 * default `error.message` is the useless "Edge Function returned a non-2xx
 * status code"; the actual reason lives in the response body on `context`.
 */
export async function readFunctionError(error: any): Promise<string> {
  const generic = error?.message || "Request failed";
  const ctx = error?.context;
  try {
    if (ctx && typeof ctx.clone === "function") {
      const text = await ctx.clone().text();
      if (text) {
        try {
          const parsed = JSON.parse(text);
          const msg = parsed?.error || parsed?.message || parsed?.msg;
          if (msg) return `${msg}${ctx.status ? ` (HTTP ${ctx.status})` : ""}`;
        } catch {
          return `${text}${ctx.status ? ` (HTTP ${ctx.status})` : ""}`;
        }
      }
    }
    if (typeof ctx?.body === "string" && ctx.body) return ctx.body;
  } catch {
    /* fall through to the generic message */
  }
  return ctx?.status ? `${generic} (HTTP ${ctx.status})` : generic;
}

/**
 * Calls a privileged database function through the Clerk-verified `secure-rpc`
 * edge function. The caller's identity is derived server-side from the Clerk
 * session token, so `_clerk_user_id` can never be spoofed from the browser.
 *
 * Returns `{ data, error }` with the same shape as `supabase.rpc()` so call
 * sites can swap over with minimal changes.
 */
export async function secureRpc<T = any>(
  fn: string,
  args: Record<string, unknown>,
  getToken?: (options?: any) => Promise<string | null>,
): Promise<{ data: T | null; error: { message: string } | null }> {
  try {
    const token =
      (await getToken?.().catch(() => null)) ||
      (await (window as any)?.Clerk?.session?.getToken?.().catch(() => null)) ||
      null;
    const { data, error } = await supabase.functions.invoke("secure-rpc", {
      body: { fn, args },
      ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    });

    if (error) return { data: null, error: { message: await readFunctionError(error) } };
    if (!data?.success) return { data: null, error: { message: data?.error || "Request failed" } };
    return { data: (data.data ?? null) as T, error: null };
  } catch (e) {
    return { data: null, error: { message: (e as Error).message } };
  }
}
